<script setup>
import { useForm } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'

// RED-317 §8 (PR 4b) "Avalda" with "Kehtib alates": the draft becomes the
// next version. The date is checked against the server rule as soon as it
// is picked (Precognition): it may not precede the date of the version valid
// today (a later scheduled one does not count), and the draft needs its title
// (market) and period — those errors are shown by the drawer next to their
// fields. Pending edits are saved first.
const props = defineProps({
  overviewId: { type: Number, required: true },
  draftSha: { type: String, default: null },
  // Saves pending edits; resolves false when that failed or conflicts.
  beforePublish: { type: Function, required: true },
})

const emit = defineEmits(['published', 'errors'])

const isoOf = date =>
  date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : ''

const validFrom = ref(new Date())

const form = useForm('post', route('admin.market-overview.publish', props.overviewId), {
  valid_from: isoOf(validFrom.value),
  base_sha: props.draftSha,
})
form.setValidationTimeout(200)

watch(
  () => props.draftSha,
  sha => {
    form.base_sha = sha
  },
)

watch(
  () => form.errors,
  errors => emit('errors', { ...errors }),
  { deep: true },
)

const onDate = date => {
  validFrom.value = date
  form.valid_from = isoOf(date)
  form.validate('valid_from')
}

const publishing = computed(() => form.processing)

const publish = async () => {
  if (!(await props.beforePublish())) {
    return
  }
  form.base_sha = props.draftSha
  form.post(route('admin.market-overview.publish', props.overviewId), {
    preserveScroll: true,
    preserveState: true,
    only: ['overviews'],
    onSuccess: () => emit('published'),
  })
}
</script>

<template>
  <div class="flex flex-wrap items-start justify-end gap-3" data-testid="mo-publish">
    <div class="flex flex-col gap-1">
      <label for="mo-valid-from" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.publish.valid_from') }}</label>
      <DatePicker
        :model-value="validFrom"
        input-id="mo-valid-from"
        date-format="dd.mm.yy"
        show-icon
        icon-display="input"
        :invalid="Boolean(form.errors.valid_from)"
        class="w-44"
        data-testid="mo-valid-from"
        @update:model-value="onDate"
      />
      <span class="w-44 text-xs text-red-600" :class="{ invisible: !form.errors.valid_from }" data-testid="mo-valid-from-error">{{ form.errors.valid_from ?? '·' }}</span>
    </div>
    <Button
      type="button"
      icon="pi pi-check"
      class="mt-5"
      :label="publishing ? $t('messages.admin.market_overviews.publish.publishing') : $t('messages.admin.market_overviews.publish.publish')"
      :loading="publishing"
      :disabled="!draftSha"
      data-testid="mo-publish-button"
      @click="publish"
    />
  </div>
</template>
